"use server";

// Auth server actions (PLG: reps self-serve). Cookies are set server-side via
// the Supabase server client; the proxy refreshes the session thereafter.

import { headers } from "next/headers";
import { redirect } from "next/navigation";
import { createSupabaseServerClient } from "@/lib/platform/supabase/server";
import { resolveSiteUrl } from "@/lib/platform/auth/recovery";

export type AuthState = { error: string } | { notice: string } | undefined;

export async function login(
  _prev: AuthState,
  formData: FormData,
): Promise<AuthState> {
  const email = String(formData.get("email") ?? "").trim();
  const password = String(formData.get("password") ?? "");
  const supabase = await createSupabaseServerClient();
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { error: error.message };
  redirect("/today");
}

export async function signup(
  _prev: AuthState,
  formData: FormData,
): Promise<AuthState> {
  const email = String(formData.get("email") ?? "").trim();
  const password = String(formData.get("password") ?? "");
  const firstName = String(formData.get("first_name") ?? "").trim();
  if (password.length < 8) return { error: "Use at least 8 characters for your password." };
  // The confirmation link must come back through /auth/confirm (which turns it
  // into a session) on this site. Same rule as password reset: production uses
  // only NEXT_PUBLIC_SITE_URL, never the request's Origin header.
  const site = resolveSiteUrl(process.env, (await headers()).get("origin"));
  if (!site) return { error: "Sign-up isn't available right now." };
  const supabase = await createSupabaseServerClient();
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { first_name: firstName },
      emailRedirectTo: `${site}/auth/confirm?next=/today`,
    },
  });
  if (error) return { error: error.message };
  // Local dev auto-confirms (session present → straight in). With confirmations
  // on (hosted), the rep opens the emailed link, which signs them in.
  if (data.session) redirect("/today");
  return { notice: "Account created. Check your email for a link to finish signing up." };
}

// Single entry the form calls; branches on the hidden `mode` field.
export async function authenticate(
  prev: AuthState,
  formData: FormData,
): Promise<AuthState> {
  return formData.get("mode") === "signup"
    ? signup(prev, formData)
    : login(prev, formData);
}

export async function logout(): Promise<void> {
  const supabase = await createSupabaseServerClient();
  await supabase.auth.signOut();
  redirect("/login");
}
